import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {Location} from '../location.model';
import {endpoints} from '../../../core/configs/endpoints';

@Injectable({providedIn: 'root'})
export class LocationApiService {
  private readonly _httpClient = inject(HttpClient);

  /**
   * Retrieves all locations of the current user.
   * @returns An Observable emitting the user's locations.
   */
  public getLocations(): Observable<{ status: string; locations: Location[] }> {
    return this._httpClient.get<{ status: string; locations: Location[] }>(
      `${endpoints.location.list}`
    );
  }

  /**
   * Retrieves details of a specific location.
   * @param locationId - The ID of the location to retrieve.
   * @returns An Observable emitting the location details.
   */
  public getLocation(locationId: string): Observable<{ status: string; location: Location }> {
    return this._httpClient.get<{ status: string; location: Location }>(
      `${endpoints.location.location}/${locationId}`
    );
  }

  /**
   * Creates a new location.
   * @param location - The location data.
   * @returns An Observable emitting the created location.
   */
  public createLocation(location: Partial<Location>): Observable<{
    status: string;
    message: string;
    result: { location: Location }
  }> {
    return this._httpClient.post<{ status: string; message: string; result: { location: Location } }>(
      `${endpoints.location.create}`,
      location
    );
  }

  /**
   * Edits an existing location.
   * @param location - The updated location data.
   * @returns An Observable emitting the update status.
   */
  public editLocation(location: Partial<Location>): Observable<{
    status: string;
    message: string;
    location: Location
  }> {
    return this._httpClient.post<{ status: string; message: string; location: Location }>(
      `${endpoints.location.edit}`,
      location
    );
  }

  /**
   * Deletes a location.
   * @param locationId - The ID of the location to delete.
   * @returns An Observable emitting the deletion status.
   */
  public deleteLocation(locationId: string): Observable<{ status: string; message: string }> {
    return this._httpClient.post<{ status: string; message: string }>(
      `${endpoints.location.delete}`, {locationId: locationId},
    );
  }

  /**
   * Assigns a location to a collection item.
   * @param locationId - The ID of the location.
   * @param collectionItemId - The ID of the collection item.
   * @param quantity - quantity to assign. //TODO: dismiss
   * @param collectionItemIds - The ID list of collection items to be assigned
   * @returns An Observable emitting the assignment status.
   */
  public assignLocation(locationId: number, collectionItemId: number,  quantity: number, collectionItemIds: number[]): Observable<{ result : {status: string; message: string} }> {
    return this._httpClient.post<{ result : {status: string; message: string} }>(
      endpoints.location.assign, {locationId, collectionItemId, quantity, collectionItemIds},
    );
  }

  // public assignLocation(locationId: number, collectionItemId: number, quantity: number): Observable<{ result : {status: string; message: string} }> {
  //   return this._httpClient.post<{ result : {status: string; message: string} }>(
  //     endpoints.location.assign, {params: {locationId, collectionItemId, quantity}},
  //   );
  // }

  /**
   * Unassigns a location from a collection item.
   * @param collectionItemId - The ID of the collection-item.
   * @returns An Observable emitting the unassignment status.
   */
  public unassignLocation(collectionItemId: string): Observable<{ result: {status: string; message: string} }> {
    return this._httpClient.post<{ result: {status: string; message: string} }>(
      endpoints.location.unassign, {collectionItemId} //TODO: implementare unassign dei singoli collectionItemId
    );
  }
}
